import PropTypes from "prop-types";
import React from "react";
import ViewList from "./ViewList";
import Settings from "./Settings";
import LayerList from "./LayerList";
import Account from "./Account";
import Help from "./Help";
import ReservationPane from "./ReservationPane";
import ObjectInfo from "./ObjectInfo";
import OrderInfo from "./OrderInfo";
import AdminPane from "./AdminPane";
import PinboardPane from "./PinboardPane";
import Tooltip from "./Tooltip";
import ErrorBoundary from "./ErrorBoundary";
import { LDFlagSet, withLDConsumer } from "launchdarkly-react-client-sdk";
import { Menu as MenuModel } from "../models/Menu";
import { Selection as SelectionModel } from "../models/Selection";
import { TimeEdit } from "../lib/TimeEdit";
import Mousetrap from "@timeedit/mousetrap";
import Language from "../lib/Language";
import _ from "underscore";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { View } from "../models/View";
import { Macros } from "../models/Macros";
import { Entry } from "../models/Entry";
import { TObjectIdClass } from "../types/api/APIClassTypes";
import { TReservationLayer } from "../types/models/TReservationLayer";

const TOOLTIP_WIDTH_MULTIPLIER = 5;

type TMenuProps = {
    data: MenuModel;
    multiPanelOrder: ("object" | "reservation" | "order")[];
    appHeight: number;
    appWidth: number;
    activeCalendar: any;
    publicSelection: Selection;
    showSelection: boolean;
    position: "left" | "right";
    onSelectionToggle: (event: React.BaseSyntheticEvent) => void;
    showAbout: (supportInfoLines?: string[], showSupportText?: boolean) => void;
    showShortcuts: () => void;
    onSettingsVisibilityChange: (value: any) => void;
    onViewChange: (id: number) => void;
    view: View;
    layers: TReservationLayer[];
    activeLayer: number;
    setActiveLayer: (layerId: number) => void;
    onLayerCreated: (layerId: number, move: boolean) => void;
    onRemoveLayer: () => void;
    onPersistLayer: (
        ignoreUnchangedReservations: boolean,
        allowDoubleReservations: boolean
    ) => void;
    reloadLayers: (callback?: () => void) => void;
    getLayerName: (suggestedName?: string) => string | false | undefined;
    getCalendarIndex: (calendar: any) => number;
    macroManager: Macros;
    onDragEnd: () => void;
    reservationIds: number[];
    dynamicReservationIds: number[];
    orderIds: number[];
    isEntryInfo: boolean;
    isInfoEntryEdited?: boolean | undefined;
    onReservationEditChange: (isEdited: boolean) => void;
    onEntryInfoOpen: (
        entry: Entry | Entry[] | number[] | null,
        showPanel: boolean,
        editMode: boolean
    ) => void;
    hasAvailableUpdate: boolean;
    resetHelp: () => void;
    types?: number[] | undefined;
    objectIds: number[];
    onReservationInfoClose: () => void;
    onDynamicReservationInfoClose: () => void;
    onObjectInfo: (
        object: TObjectIdClass,
        createCopy: boolean | undefined,
        viewOnly: boolean | undefined,
        title: string
    ) => void;
    onObjectInfoClose: () => void;
    onOrderInfoClose: () => void;
    flags: LDFlagSet;
    useNewUI: boolean;
    useTEUIStep2: boolean;
} & TAppContextProp;

type TMenuState = { showTooltips: Record<number, boolean> };

class Menu extends React.Component<TMenuProps, TMenuState> {
    static propTypes: any = {
        data: PropTypes.instanceOf(MenuModel).isRequired,
        selection: PropTypes.instanceOf(SelectionModel),
        onReservationEditChange: PropTypes.func,
        useNewUI: PropTypes.bool,
    };

    state = { showTooltips: {} };

    componentDidMount() {
        Mousetrap.bindWithHelp(
            "shift+mod+l",
            (e) => this.props.onSelectionToggle(e),
            undefined,
            Language.get("nc_menu_open/close_lists.")
        );

        const activeMenuId = this.props.data.activeMenu;
        if (activeMenuId === "settings" && this.props.data.isOpen) {
            this.props.onSettingsVisibilityChange(true);
        }
    }

    _previousEscBinding: any;

    componentWillUnmount() {
        Mousetrap.unbindWithHelp("esc");
        if (this._previousEscBinding) {
            Mousetrap.bindWithHelp("esc", this._previousEscBinding);
        }
    }

    getMenuAction = (item) => {
        if (item.id === "selection") {
            return this.props.onSelectionToggle;
        }

        return this.toggleMenu.bind(this, item);
    };

    open = (id) => {
        this.props.onSettingsVisibilityChange(id === "settings");
        this.props.appContext.update(this.props.data, this.props.data.open(id));
        this._previousEscBinding = Mousetrap.unbindWithHelp("esc", true)[0];
        Mousetrap.bindWithHelp(
            "esc",
            () => {
                if (this.props.data.isOpen) {
                    this.close();
                }
            },
            undefined,
            Language.get("nc_menu_close_menu.")
        );
    };

    close = () => {
        this.props.onSettingsVisibilityChange(false);
        this.props.appContext.update(this.props.data, this.props.data.close());
        Mousetrap.unbindWithHelp("esc");
        if (this._previousEscBinding) {
            Mousetrap.bindWithHelp("esc", this._previousEscBinding);
        }
    };

    toggleMenu = (item) => {
        if (this.props.data.isOpen && item.id === this.props.data.activeMenu) {
            this.close();
            return;
        }

        this.open(item.id);
    };

    showTooltip = (id) => {
        const showTooltips = this.state.showTooltips || {};
        showTooltips[id] = true;
        this.setState({ showTooltips });
    };

    hideTooltip = (id) => {
        const showTooltips = this.state.showTooltips || {};
        showTooltips[id] = false;
        this.setState({ showTooltips });
    };

    isTooltipVisible = (id) => {
        if (!this.state.showTooltips || !this.state.showTooltips[id]) {
            return false;
        }

        return this.state.showTooltips[id];
    };

    getCurrentComponent = () => {
        const item = this.props.data.getCurrentItem();
        if (!item) {
            return null;
        }

        switch (item.id) {
            case "account":
                return (
                    <Account
                        key={item.id}
                        useNewUI={this.props.useNewUI}
                        flags={this.props.flags}
                    />
                );
            case "settings":
                return (
                    <ErrorBoundary>
                        <Settings
                            key={item.id}
                            calendar={this.props.activeCalendar}
                            publicSelection={this.props.publicSelection}
                            getCalendarIndex={this.props.getCalendarIndex}
                        />
                    </ErrorBoundary>
                );
            case "views":
                return (
                    <ErrorBoundary>
                        <ViewList
                            key={item.id}
                            data={item.model}
                            onViewChange={this.props.onViewChange}
                            view={this.props.view}
                        />
                    </ErrorBoundary>
                );
            case "layers":
                return (
                    <LayerList
                        data={item.model}
                        layers={this.props.layers}
                        layer={this.props.activeLayer}
                        setActiveLayer={this.props.setActiveLayer}
                        onLayerCreated={this.props.onLayerCreated}
                        onRemoveLayer={this.props.onRemoveLayer}
                        onPersistLayer={this.props.onPersistLayer}
                        reloadLayers={this.props.reloadLayers}
                        getLayerName={this.props.getLayerName}
                    />
                );
            case "info":
                if (
                    this.props.reservationIds.length === 0 &&
                    this.props.orderIds.length === 0 &&
                    this.props.objectIds.length === 0 &&
                    this.props.dynamicReservationIds.length === 0
                ) {
                    return (
                        <div style={{ padding: "10px" }}>
                            <h2>{Language.get("cal_res_side_tab_res_info")}</h2>
                            <p>{Language.get("nc_menu_reservation_not_selected")}</p>
                        </div>
                    );
                }
                // eslint-disable-next-line no-case-declarations
                const dynamicReservationIds = this.props.dynamicReservationIds.filter(
                    (id) => this.props.reservationIds.indexOf(id) === -1
                );
                // eslint-disable-next-line no-case-declarations
                const panels = this.props.multiPanelOrder.map((panelName) => {
                    switch (panelName) {
                        case "object":
                            return (
                                <ObjectInfo
                                    key={panelName}
                                    embedded={true}
                                    noIgnore={true}
                                    colorDefs={this.props.appContext.colorDefs}
                                    typeId={TimeEdit.rootType}
                                    objectId={this.props.objectIds ? this.props.objectIds[0] : 0}
                                    onClose={this.props.onObjectInfoClose}
                                    onObjectInfo={this.props.onObjectInfo}
                                    user={this.props.appContext.user}
                                />
                            );
                        case "reservation":
                            return (
                                <ReservationPane
                                    key={panelName}
                                    user={this.props.appContext.user}
                                    activeLayer={this.props.activeLayer}
                                    reservationIds={this.props.reservationIds}
                                    isInfoEntryEdited={this.props.isInfoEntryEdited}
                                    onReservationEditChange={this.props.onReservationEditChange}
                                    onEntryInfoOpen={this.props.onEntryInfoOpen}
                                    isEntryInfo={this.props.isEntryInfo}
                                    onClose={this.props.onReservationInfoClose}
                                    onObjectInfo={this.props.onObjectInfo}
                                />
                            );
                        case "order":
                            return (
                                <OrderInfo
                                    key={panelName}
                                    orderIds={this.props.orderIds}
                                    onClose={this.props.onOrderInfoClose}
                                />
                            );
                        default:
                            return <></>;
                    }
                });
                return (
                    <div style={{ position: "relative", height: "100%", overflow: "hidden" }}>
                        <div className="multiPane" style={{ overflow: "auto", height: "100%" }}>
                            {panels}
                        </div>
                        <ReservationPane
                            dynamic={true}
                            user={this.props.appContext.user}
                            activeLayer={this.props.activeLayer}
                            reservationIds={dynamicReservationIds}
                            isEntryInfo={this.props.isEntryInfo}
                            onClose={this.props.onDynamicReservationInfoClose}
                            onObjectInfo={this.props.onObjectInfo}
                        />
                    </div>
                );
            case "help":
                return (
                    <Help
                        key={item.id}
                        showAbout={this.props.showAbout}
                        showShortcuts={this.props.showShortcuts}
                        hasAvailableUpdate={this.props.hasAvailableUpdate}
                        resetHelp={this.props.resetHelp}
                    />
                );
            case "pinboard":
                return (
                    <PinboardPane
                        key={item.id}
                        showExtraInfo={this.props.appContext.user?.showExtraInfo ?? false}
                        scratchpad={this.props.appContext.scratchpad}
                    />
                );
            case "admin":
                return <AdminPane key={item.id} />;
            default:
                return <></>;
        }
    };

    onDragStart = (evt: any) => {
        _.setDragData(evt, "application/x-timeedit-menu", "");
    };

    onDragEnd = () => {
        this.props.onDragEnd();
    };

    getItems = (useNewUI: boolean) => {
        if (useNewUI) {
            return this.props.data.items.filter(
                (item) =>
                    item.id !== "views" &&
                    item.id !== "admin" &&
                    item.id !== "help" &&
                    !(this.props.useTEUIStep2 && item.id === "layers") &&
                    !(this.props.useTEUIStep2 && item.id === "pinboard") &&
                    !(this.props.useTEUIStep2 && item.id === "account")
            );
        }
        return this.props.data.items;
    };

    render() {
        const menuItems = this.getItems(this.props.useNewUI).map(this.renderMenuItem);

        const isRight = this.props.position === MenuModel.POSITION.RIGHT;

        const classes = {
            popover: true,
            hidden: !this.props.data.isOpen,
            right: isRight,
            left: !isRight,
        };

        const menuClasses = {
            right: isRight,
            left: !isRight,
        };

        return (
            <div id="menuWrapper">
                <ul
                    id="menu"
                    style={{ height: this.props.appHeight }}
                    className={_.classSet(menuClasses)}
                    draggable={true}
                    onDragStart={this.onDragStart}
                    onDragEnd={this.onDragEnd}
                >
                    {menuItems}
                </ul>

                <div
                    id="menuPopover"
                    style={{ height: this.props.appHeight }}
                    className={_.classSet(classes)}
                >
                    {this.props.data.isOpen ? this.getCurrentComponent() : null}
                </div>
            </div>
        );
    }

    renderMenuItem = (item) => {
        const isActiveItem = item.id === this.props.data.activeMenu;
        const classes: Partial<Record<string, boolean>> = {
            active: this.props.data.isOpen && isActiveItem,
            indicator: this.props.hasAvailableUpdate,
        };
        classes[item.id] = true;

        if (item.id === "settings" && this.props.useTEUIStep2) {
            delete classes.settings;
            classes.viewSettings = true;
        }

        const tooltip = Language.get(item.label);

        const isRight = this.props.position === MenuModel.POSITION.RIGHT;

        return (
            <li
                id={item.id}
                ref={item.id}
                key={item.id}
                className={_.classSet(classes)}
                onClick={this.getMenuAction(item)}
                onMouseOver={this.showTooltip.bind(this, item.id)}
                onMouseOut={this.hideTooltip.bind(this, item.id)}
            >
                <Tooltip
                    className="tooltip"
                    height={26}
                    width={tooltip.length * TOOLTIP_WIDTH_MULTIPLIER}
                    horizontalPosition={isRight ? "right" : "left"}
                    verticalPosition="top"
                    show={this.isTooltipVisible(item.id)}
                >
                    <a />
                    <span>{tooltip}</span>
                </Tooltip>
            </li>
        );
    };
}

export default withLDConsumer()(withAppContext(Menu));
